// MARK://dhk8/admin-shell/stores/theme
// Manual light/dark toggle for the admin shell (NOT prefers-color-scheme).
// Mirrors apps/dhk8-pos/src/stores/theme.store.ts — copied, not imported.
// Persists to localStorage `dhk8-admin-theme`. Default `'dark'` (Restaurant Dark).
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ColorTheme = 'light' | 'dark'

const STORAGE_KEY = 'dhk8-admin-theme'

function applyToHtml(theme: ColorTheme): void {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  html.classList.remove('light', 'dark', 'hig', 'black')
  html.classList.add(theme)
  html.dataset['theme'] = theme
  html.style.colorScheme = theme
}

function migrateTheme(raw: string | null): ColorTheme {
  return raw === 'light' ? 'light' : 'dark'
}

interface ThemeState {
  theme: ColorTheme
  setTheme: (t: ColorTheme) => void
  toggleTheme: () => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      setTheme: (theme) => { set({ theme }); applyToHtml(theme) },
      toggleTheme: () => {
        const next: ColorTheme = get().theme === 'dark' ? 'light' : 'dark'
        set({ theme: next }); applyToHtml(next)
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (s) => ({ theme: s.theme }),
      storage: {
        getItem: () => {
          if (typeof localStorage === 'undefined') return { state: { theme: 'dark' as ColorTheme }, version: 0 }
          return { state: { theme: migrateTheme(localStorage.getItem(STORAGE_KEY)) }, version: 0 }
        },
        setItem: (_name, value) => {
          if (typeof localStorage === 'undefined') return
          const v = value as { state: { theme: ColorTheme } }
          localStorage.setItem(STORAGE_KEY, v.state.theme)
        },
        removeItem: () => { if (typeof localStorage !== 'undefined') localStorage.removeItem(STORAGE_KEY) },
      },
      onRehydrateStorage: () => (state) => { applyToHtml(state?.theme ?? 'dark') },
    },
  ),
)

/** Call once at boot so <html> attrs match the persisted theme before first paint. */
export function initTheme(): void {
  applyToHtml(useThemeStore.getState().theme)
}
